import React, { useEffect, useState } from 'react';
import { Plus, Search, Lock, Unlock, ChevronDown } from 'lucide-react';
import api from '../../utils/api';

const STATUS_COLORS = { ativo: 'bg-green-100 text-green-700', bloqueado: 'bg-red-100 text-red-700', trial: 'bg-amber-100 text-amber-700' };

export default function AdminTenants() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ nome: '', tipo: 'imobiliaria', plano: 'free', email_responsavel: '' });

  const load = () => {
    setLoading(true);
    api.get('/admin/tenants', { params: { q: q || undefined } }).then(r => setTenants(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [q]);

  const create = async (e) => {
    e.preventDefault();
    await api.post('/admin/tenants', form);
    setShowCreate(false);
    setForm({ nome: '', tipo: 'imobiliaria', plano: 'free', email_responsavel: '' });
    load();
  };

  const toggle = async (t) => {
    if (t.status === 'ativo') await api.post(`/admin/tenants/${t.id}/bloquear`);
    else await api.post(`/admin/tenants/${t.id}/reativar`);
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-stone-900">Tenants</h1>
        <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 bg-[#0f3d2e] text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-[#0a2e22] transition-colors">
          <Plus size={16} /> Novo Tenant
        </button>
      </div>

      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar por nome..."
          className="w-full pl-9 pr-4 py-2.5 border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-[#0f3d2e]" />
      </div>

      <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-stone-500 text-xs">
            <tr>
              <th className="text-left px-4 py-3">Nome</th>
              <th className="text-left px-4 py-3">Tipo</th>
              <th className="text-left px-4 py-3">Plano</th>
              <th className="text-left px-4 py-3">Usuários</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-50">
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-stone-400">Carregando...</td></tr>
            ) : tenants.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-stone-400">Nenhum tenant encontrado</td></tr>
            ) : tenants.map(t => (
              <tr key={t.id} className="hover:bg-stone-50">
                <td className="px-4 py-3 font-medium text-stone-800">{t.nome}</td>
                <td className="px-4 py-3 text-stone-500 capitalize">{t.tipo}</td>
                <td className="px-4 py-3 text-stone-500 capitalize">{t.plano}</td>
                <td className="px-4 py-3 text-stone-500">{t.usuarios_count ?? 0}</td>
                <td className="px-4 py-3"><span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_COLORS[t.status] || 'bg-stone-100 text-stone-600'}`}>{t.status}</span></td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => toggle(t)} title={t.status === 'ativo' ? 'Bloquear' : 'Reativar'}
                    className="p-1.5 rounded-md hover:bg-stone-100 text-stone-500">
                    {t.status === 'ativo' ? <Lock size={14} /> : <Unlock size={14} />}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-xl">
            <div className="px-6 py-4 border-b border-stone-100 font-semibold text-stone-800">Novo Tenant</div>
            <form onSubmit={create} className="p-6 space-y-4">
              <div><label className="text-xs font-medium text-stone-600 block mb-1">Nome *</label>
                <input required value={form.nome} onChange={e => setForm(f => ({...f, nome: e.target.value}))} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#0f3d2e]" /></div>
              <div><label className="text-xs font-medium text-stone-600 block mb-1">Tipo *</label>
                <select value={form.tipo} onChange={e => setForm(f => ({...f, tipo: e.target.value}))} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#0f3d2e]">
                  <option value="corretor">Corretor</option>
                  <option value="imobiliaria">Imobiliária</option>
                  <option value="construtora">Construtora</option>
                </select></div>
              <div><label className="text-xs font-medium text-stone-600 block mb-1">Plano</label>
                <select value={form.plano} onChange={e => setForm(f => ({...f, plano: e.target.value}))} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#0f3d2e]">
                  {['free','premium','imobiliaria','construtora','enterprise'].map(p => <option key={p} value={p}>{p}</option>)}
                </select></div>
              <div><label className="text-xs font-medium text-stone-600 block mb-1">E-mail responsável</label>
                <input type="email" value={form.email_responsavel} onChange={e => setForm(f => ({...f, email_responsavel: e.target.value}))} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#0f3d2e]" /></div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="flex-1 border border-stone-200 rounded-lg py-2.5 text-sm font-medium text-stone-600 hover:bg-stone-50">Cancelar</button>
                <button type="submit" className="flex-1 bg-[#0f3d2e] text-white rounded-lg py-2.5 text-sm font-medium hover:bg-[#0a2e22]">Criar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
